import React, { useEffect, useState } from "react";
import { SectionCard, SourceLink, money } from "./common";
import { TField, TSelect } from "./forms";
import { GoldButton, OutlineButton, EmptyState, ui } from "@/components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Plus, Wrench, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_COLORS = { Low: "#9FB8C9", Medium: "#C6A45C", High: "#B8860B", Urgent: "#b06b6b" };
const STATUS_COLORS = { Open: "#C6A45C", "In Progress": "#9FB8C9", Completed: "#8FB996", "On Hold": "#B79B86" };

export function MaintenanceTab({ property, currency, options }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blank());
  function blank() {
    return { date: new Date().toISOString().slice(0, 10), category: "Repairs", priority: "Medium",
      description: "", contractor: "", status: "Open", cost: "", warranty_expiry: "", notes: "" };
  }
  const load = () => api.get(`/properties/${property.id}/maintenance`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [property.id]);

  const add = async () => {
    if (!form.description) return toast.error("Add a description");
    setSaving(true);
    try {
      await api.post(`/properties/${property.id}/maintenance`, { ...form, cost: Number(form.cost) || 0 });
      toast.success("Maintenance logged"); setOpen(false); setForm(blank()); load();
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/properties/${property.id}/maintenance/${id}`); load(); };

  return (
    <SectionCard title="Maintenance Register" action={
      <GoldButton data-testid="maint-add" onClick={() => setOpen(true)}><Plus size={15} /> Log maintenance</GoldButton>
    }>
      {items.length === 0 ? (
        <EmptyState icon={Wrench} title="No maintenance logged" hint="Track repairs, contractors, costs and warranties for this property." />
      ) : (
        <div className="space-y-3" data-testid="maint-list">
          {items.map((m) => (
            <div key={m.id} data-testid={`maint-${m.id}`} className="bg-white/60 border border-[#EAE0D2] rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-[#2C2A28] font-medium">{m.description}</span>
                    <Badge style={{ background: `${PRIORITY_COLORS[m.priority]}22`, color: PRIORITY_COLORS[m.priority], borderColor: `${PRIORITY_COLORS[m.priority]}55` }}>{m.priority}</Badge>
                    <Badge style={{ background: `${STATUS_COLORS[m.status]}22`, color: STATUS_COLORS[m.status], borderColor: `${STATUS_COLORS[m.status]}55` }}>{m.status}</Badge>
                  </div>
                  <div className="text-xs text-[#A99F90] mt-1">
                    {m.date} · {m.category}{m.contractor ? ` · ${m.contractor}` : ""}{m.warranty_expiry ? ` · warranty to ${m.warranty_expiry}` : ""}
                  </div>
                  {m.invoice_document_id && <div className="mt-1"><SourceLink docId={m.invoice_document_id} testid={`maint-source-${m.id}`} /></div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono-data text-sm text-[#2C2A28]">{money(m.cost, currency)}</div>
                  <button data-testid={`maint-del-${m.id}`} onClick={() => remove(m.id)} className="text-[#b06b6b] hover:bg-[#C99FA0]/10 rounded-lg p-1 mt-1"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`${ui.dialog} max-w-2xl`}>
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Log maintenance</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <TField label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="maint-date" />
            <TSelect label="Category" value={form.category} options={["Repairs", "Plumbing", "Electrical", "Appliance", "Garden", "Pest", "Inspection", "Other"]} onChange={(v) => setForm({ ...form, category: v })} testid="maint-category" />
            <TSelect label="Priority" value={form.priority} options={options.maint_priority || []} onChange={(v) => setForm({ ...form, priority: v })} testid="maint-priority" />
            <TSelect label="Status" value={form.status} options={options.maint_status || []} onChange={(v) => setForm({ ...form, status: v })} testid="maint-status" />
            <TField label="Contractor" value={form.contractor} onChange={(v) => setForm({ ...form, contractor: v })} testid="maint-contractor" />
            <TField label="Cost" type="number" value={form.cost} onChange={(v) => setForm({ ...form, cost: v })} testid="maint-cost" />
            <TField label="Warranty Expiry" type="date" value={form.warranty_expiry} onChange={(v) => setForm({ ...form, warranty_expiry: v })} testid="maint-warranty" />
            <div className="col-span-2">
              <TField label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="maint-description" />
            </div>
          </div>
          <DialogFooter>
            <OutlineButton onClick={() => setOpen(false)}>Cancel</OutlineButton>
            <GoldButton data-testid="maint-save" onClick={add} disabled={saving}>{saving ? <Loader2 size={15} className="animate-spin" /> : null} Save</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
